import React, { useState } from "react";
import { Form, Input, Button, Upload, Row, Col, Modal, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../lib/firebase';
import applicationServices from '../lib/applicationServices';

const ApplicationPortal = () => {
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [user, authLoading] = useAuthState(auth);

  const normFile = (e) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e && e.fileList;
  };

  const onFinish = async (values) => {
    // Check if user is authenticated
    if (!user) {
      message.error('Please sign in to submit an application');
      return;
    }

    // Check if resume file is uploaded
    if (!values.resume || values.resume.length === 0) {
      message.error('Please upload your resume');
      return;
    }

    setLoading(true);
    
    try {
      const resumeFile = values.resume[0].originFileObj;
      
      // Submit application to backend
      const result = await applicationServices.submitApplication(
        {
          firstName: values.firstName,
          lastName: values.lastName,
          position: values.position,
          expertise: values.expertise
        },
        resumeFile,
        user
      );

      if (result.success) {
        // Show success modal
        setIsModalVisible(true);
        // Reset form
        form.resetFields();
        message.success('Application submitted successfully!');
      } else {
        message.error(`Submission failed: ${result.error}`);
      }
    } catch (error) {
      console.error('Application submission error:', error);
      message.error(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOk = () => {
    setIsModalVisible(false);
  };

  // Custom validator for file upload
  const validateFile = (_, value) => {
    if (!value || value.length === 0) {
      return Promise.reject(new Error("Please upload your resume PDF"));
    }
    
    const file = value[0];
    const isPdf = file?.name?.toLowerCase().endsWith(".pdf");
    
    if (!isPdf) {
      return Promise.reject(new Error("Only PDF files are allowed"));
    }

    // Validate file size (5MB limit)
    const maxSize = 5 * 1024 * 1024; // 5MB in bytes
    if (file.size > maxSize) {
      return Promise.reject(new Error("File size must be less than 5MB"));
    }
    
    return Promise.resolve();
  };

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div
        style={{
          maxWidth: 600,
          margin: "30px auto",
          padding: 24,
          textAlign: "center",
        }}
      >
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <div
      style={{
        maxWidth: 600,
        margin: "30px auto",
        padding: 24,
        border: "1px solid #ccc",
        fontFamily: "Arial, sans-serif",
      }}
    >
      <h3 style={{ textAlign: "center", fontWeight: "bold" }}>
        Application Portal
      </h3>

      {/* Authentication Status */}
      {!user && (
        <div style={{ 
          background: '#fff2e8', 
          border: '1px solid #ffbb96', 
          padding: '12px', 
          marginBottom: '16px',
          borderRadius: '4px',
          textAlign: 'center'
        }}>
          <p style={{ margin: 0 }}>
            <strong>Please sign in to submit your application.</strong>
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>
            You need to be logged in to apply for positions.
          </p>
        </div>
      )}

      <Form 
        form={form} 
        layout="vertical" 
        onFinish={onFinish}
        disabled={!user || loading}
      >
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="First Name"
              name="firstName"
              rules={[{ required: true, message: "Please enter first name" }]}
            >
              <Input placeholder="Enter your first name" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Last Name"
              name="lastName"
              rules={[{ required: true, message: "Please enter last name" }]}
            >
              <Input placeholder="Enter your last name" />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              label="Position Applied For"
              name="position"
              rules={[
                { required: true, message: "Please enter position applied for" },
              ]}
            >
              <Input placeholder="e.g., Product Manager, Football Refree" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              label="Upload your Resume in PDF"
              name="resume"
              valuePropName="fileList"
              getValueFromEvent={normFile}
              rules={[
                { 
                  required: true, 
                  validator: validateFile 
                }
              ]}
              extra="Maximum file size: 5MB"
            >
              <Upload
                name="resume"
                accept=".pdf"
                maxCount={1}
                beforeUpload={() => false}
                showUploadList={{ 
                  showRemoveIcon: true,
                  showDownloadIcon: false
                }}
                disabled={!user}
              >
                <Button 
                  icon={<UploadOutlined />} 
                  disabled={!user}
                >
                  Click to Upload PDF
                </Button>
              </Upload>
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          label="Summarize your technical expertise"
          name="expertise"
          rules={[
            { required: true, message: "Please summarize your technical expertise" },
            { min: 50, message: "Please provide at least 50 characters about your expertise" }
          ]}
          extra="Please describe your skills, technologies you're proficient with, and relevant experience (minimum 50 characters)"
        >
          <Input.TextArea 
            rows={6} 
            placeholder="E.g., 3 years of experience in product manager in abc company..."
            showCount
            minLength={50}
            maxLength={1000}
          />
        </Form.Item>

        <Form.Item>
          <Button
            type="primary"
            htmlType="submit"
            loading={loading}
            disabled={!user}
            style={{
              backgroundColor: "#5c007a",
              borderColor: "#5c007a",
              color: "#fff",
              fontWeight: "bold",
              width: "100%",
              height: "40px",
            }}
          >
            {loading ? 'Submitting...' : (user ? 'Submit Application' : 'Please Sign In to Apply')}
          </Button>
        </Form.Item>
      </Form>

      <Modal
        title="Application Submitted Successfully"
        visible={isModalVisible}
        onOk={handleOk}
        footer={[
          <Button 
            key="ok" 
            type="primary" 
            onClick={handleOk}
            style={{
              backgroundColor: "#5c007a",
              borderColor: "#5c007a",
            }}
          >
            OK
          </Button>,
        ]}
        closable={false}
      >
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '16px', marginBottom: '16px' }}>
            <strong>Thank you for your application!</strong>
          </p>
          <p>
            Your application has been successfully submitted to our system. 
            Our HR team will review it and get in touch with you soon.
          </p>
          <p style={{ fontStyle: 'italic', marginTop: '16px' }}>
            You can check the status of your application in your dashboard.
          </p>
        </div>
      </Modal>
    </div>
  );
};

export default ApplicationPortal;